// Kindra Health · Concepts index · PREVIEW ONLY
// Simple 3-card picker so the founder can compare the three homepage
// directions side-by-side. Not a public page — not linked from the live
// homepage. Accessed via direct URL /concepts.

import React from 'react';
import { Link } from 'react-router-dom';
import {
  PAPER, NAVY, NAVY_MUTED, NAVY_SOFT, TEAL, TEAL_SOFT, HAIRLINE, SAGE_BG, CORAL_SOFT,
  ArrowRight,
} from './_shared';

const CARDS = [
  {
    key: 'A',
    slug: '/concept-a',
    label: 'Concept A',
    title: 'Product Platform',
    line: 'Serious health-tech. Realistic multi-pane app mockup in the hero. Feels like a real system, not a Q&A.',
    bg: NAVY,
    fg: PAPER,
    accent: TEAL,
    tag: 'for investor credibility',
  },
  {
    key: 'B',
    slug: '/concept-b',
    label: 'Concept B',
    title: 'Human Care Companion',
    line: 'Warm, emotional, editorial. Scatter → brief transformation. Emotional relief-led. Story-first.',
    bg: SAGE_BG,
    fg: NAVY,
    accent: TEAL,
    tag: 'for 25-user pilot warmth',
  },
  {
    key: 'C',
    slug: '/concept-c',
    label: 'Concept C',
    title: 'Challenger — Generic vs Kindra',
    line: 'Split-screen comparison. Generic AI vs Kindra. Instant aha. High-conversion positioning.',
    bg: '#FFFFFF',
    fg: NAVY,
    accent: TEAL,
    tag: 'for paid acquisition',
  },
  {
    key: 'D',
    slug: '/concept-d',
    label: 'Concept D · Hybrid',
    title: 'Clarity + Product + Warmth',
    line: 'C-clarity hero + A-product-proof + B-warmth + flagship Doctor Brief section. Final candidate.',
    bg: NAVY,
    fg: PAPER,
    accent: TEAL,
    tag: 'founder recommendation',
  },
];

const ConceptsIndex = () => (
  <div className="min-h-screen font-sans" style={{ backgroundColor: PAPER, color: NAVY }} data-testid="concepts-index">
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pt-16 md:pt-24 pb-20 md:pb-28">
      <p className="font-sans text-[11px] md:text-[12px] font-medium uppercase mb-4" style={{ color: TEAL, letterSpacing: '0.26em' }}>Kindra Health · preview only</p>
      <h1 className="font-serif text-[44px] md:text-[60px] lg:text-[72px] leading-[1.04] tracking-[-0.02em] font-normal max-w-[900px]" style={{ color: NAVY }} data-testid="concepts-headline">
        Four homepage <em className="italic">directions.</em>
      </h1>
      <p className="mt-6 md:mt-8 font-sans text-[16px] md:text-[18px] leading-[1.55] max-w-[680px]" style={{ color: NAVY_MUTED }}>
        Each concept is a full preview — hero, product proof, trust, CTA and footer — on a distinct route. Concept D is the hybrid final candidate. Production is untouched.
      </p>

      <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {CARDS.map((c) => (
          <Link
            key={c.key}
            to={c.slug}
            className="group flex flex-col rounded-2xl p-7 md:p-9 min-h-[360px] transition-transform hover:-translate-y-1"
            style={{ backgroundColor: c.bg, color: c.fg, border: `1px solid ${HAIRLINE}`, boxShadow: '0 24px 48px -28px rgba(14,28,46,0.28)' }}
            data-testid={`concept-card-${c.key}`}
          >
            <div className="flex items-center justify-between mb-6">
              <span className="font-sans text-[10.5px] uppercase font-medium" style={{ color: c.accent, letterSpacing: '0.26em' }}>{c.label}</span>
              <span className="font-sans text-[10.5px]" style={{ color: c.fg === PAPER ? 'rgba(251,250,246,0.6)' : NAVY_SOFT, letterSpacing: '0.18em' }}>{c.slug}</span>
            </div>
            <p className="font-serif text-[30px] md:text-[34px] leading-tight mb-4">{c.title}</p>
            <p className="font-sans text-[15px] leading-[1.6] flex-1" style={{ color: c.fg === PAPER ? 'rgba(251,250,246,0.75)' : NAVY_MUTED }}>
              {c.line}
            </p>
            <div className="mt-6 pt-5 flex items-center justify-between" style={{ borderTop: `1px solid ${c.fg === PAPER ? 'rgba(251,250,246,0.12)' : HAIRLINE}` }}>
              <span className="font-sans text-[11.5px] uppercase" style={{ color: c.fg === PAPER ? 'rgba(251,250,246,0.55)' : NAVY_SOFT, letterSpacing: '0.22em' }}>{c.tag}</span>
              <span className="inline-flex items-center gap-1.5 font-sans text-[13.5px] font-medium group-hover:translate-x-0.5 transition-transform" style={{ color: c.accent }}>
                View preview <ArrowRight size={14} stroke={2} />
              </span>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-16 md:mt-20 font-sans text-[12.5px] max-w-[760px]" style={{ color: NAVY_SOFT, lineHeight: 1.7 }}>
        Current live homepage (V12/V13 attempt) at <Link to="/" className="underline">kindra.ai/</Link>. Production site (kindrahealth.app) is untouched — these previews only exist under /concept-a, /concept-b, /concept-c on the preview environment.
      </div>
    </div>
  </div>
);

export default ConceptsIndex;
